import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES, USER_ROLES } from '../utils/constants';
import { RequireAuth } from '../guards/RequireAuth';
import { RequireRole } from '../guards/RequireRole';
import { RequireMembership } from '../guards/RequireMembership';
import { UserRole } from '../modules/platform/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | UserRole[];
  requireOnboarding?: boolean;
}

const membershipRoles = new Set<UserRole>([
  USER_ROLES.SCHOOL_ADMIN,
  USER_ROLES.TEACHER,
  USER_ROLES.STUDENT,
  USER_ROLES.PARENT,
]);

const requiresMembership = (requiredRole?: UserRole | UserRole[]): boolean => {
  if (!requiredRole) return false;
  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  return roles.some((role) => membershipRoles.has(role));
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requireOnboarding = false,
}) => {
  const { user } = useAuth();

  const content = requiredRole ? (
    <RequireRole requiredRole={requiredRole}>
      {requiresMembership(requiredRole) ? <RequireMembership>{children}</RequireMembership> : children}
    </RequireRole>
  ) : (
    <>{children}</>
  );

  const onboardingRedirect = () => {
    if (requireOnboarding && user && !user.onboarding_complete) {
      if (user.roles.includes(USER_ROLES.SCHOOL_ADMIN)) {
        return <Navigate to={ROUTES.ONBOARDING_SCHOOL_SETUP} replace />;
      }
      if (user.roles.includes(USER_ROLES.TEACHER)) {
        return <Navigate to={ROUTES.ONBOARDING_TEACHER_PROFILE} replace />;
      }
      if (user.roles.includes(USER_ROLES.STUDENT)) {
        return <Navigate to={ROUTES.ONBOARDING_STUDENT_PROFILE} replace />;
      }
    }
    return content;
  };

  return <RequireAuth>{onboardingRedirect()}</RequireAuth>;
};

